import {
  Box, Button, CircularProgress, FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createEvent, getEvent, updateEvent } from '../../api/events';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';

interface EventFormState {
  name: string;
  description: string;
  location: string;
  priceDollars: string;
  capacity: string;
  eventDate: string;
  isActive: boolean;
}

const empty: EventFormState = {
  name: '',
  description: '',
  location: '',
  priceDollars: '0.00',
  capacity: '',
  eventDate: '',
  isActive: true,
};

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EventEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { request: getRequest } = useApiCallWithAuth(getEvent);
  const { request: createRequest } = useApiCallWithAuth(createEvent);
  const { request: updateRequest } = useApiCallWithAuth(updateEvent);

  const [form, setForm] = useState<EventFormState>(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    getRequest(id)
      .then(data => {
        setForm({
          name: data.name,
          description: data.description ?? '',
          location: data.location ?? '',
          priceDollars: (data.priceCents / 100).toFixed(2),
          capacity: String(data.capacity),
          eventDate: toDatetimeLocal(data.eventDate),
          isActive: data.isActive,
        });
      })
      .catch(() => setError('Failed to load event'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  function set<K extends keyof EventFormState>(field: K, value: EventFormState[K]) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSave() {
    setError(null);
    const priceCents = Math.round(parseFloat(form.priceDollars || '0') * 100);
    const capacity = parseInt(form.capacity, 10);

    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!Number.isFinite(priceCents) || priceCents < 0) { setError('Price must be zero or a positive amount.'); return; }
    if (!Number.isInteger(capacity) || capacity <= 0) { setError('Capacity must be a positive whole number.'); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || undefined,
        location: form.location || undefined,
        priceCents,
        capacity,
        eventDate: form.eventDate ? new Date(form.eventDate).toISOString() : undefined,
      };

      if (isEdit && id) {
        await updateRequest(id, { ...payload, isActive: form.isActive });
      } else {
        await createRequest(payload);
      }
      navigate('/events/admin');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 3 }}>
        <Typography variant='h5' fontWeight={700}>{isEdit ? 'Edit Event' : 'New Event'}</Typography>
        <Stack direction='row' spacing={1}>
          <Button variant='outlined' onClick={() => navigate('/events/admin')}>Cancel</Button>
          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Stack>
      </Stack>

      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      <Stack spacing={2.5}>
        <TextField
          label='Name'
          value={form.name}
          onChange={e => set('name', e.target.value)}
          fullWidth
          required
        />

        <TextField
          label='Description'
          value={form.description}
          onChange={e => set('description', e.target.value)}
          fullWidth
          multiline
          rows={4}
        />

        <TextField
          label='Location'
          helperText='Address, venue name, or "Virtual" — optional'
          value={form.location}
          onChange={e => set('location', e.target.value)}
          fullWidth
        />

        <TextField
          label='Price (USD)'
          helperText='Leave as 0.00 for a free event'
          type='number'
          value={form.priceDollars}
          onChange={e => set('priceDollars', e.target.value)}
          fullWidth
          required
          inputProps={{ step: '0.01', min: '0' }}
        />

        <TextField
          label='Capacity'
          type='number'
          value={form.capacity}
          onChange={e => set('capacity', e.target.value)}
          fullWidth
          required
          inputProps={{ step: '1', min: '1' }}
        />

        <TextField
          label='Event Date & Time (optional)'
          type='datetime-local'
          value={form.eventDate}
          onChange={e => set('eventDate', e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />

        {isEdit && (
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e => set('isActive', e.target.checked)}
              />
            }
            label={form.isActive ? 'Active' : 'Inactive'}
          />
        )}
      </Stack>
    </Box>
  );
}
